// Werbeplanung: reine Rechenlogik für die Seite /werbung (ohne React).
//
// Bewusst dieselben Regeln wie der Planer in apps/api/src/playout/clock-planner.ts:
//  - Zeitraum: Beginn und Ende zählen mit (Datum, ohne Uhrzeit).
//  - Tageszeit: verglichen wird der BEGINN der Stunde. „06:00–10:00“ gilt für
//    die Stunden ab 6, 7, 8 und 9 Uhr. Leer heißt „ab Mitternacht“ bzw. „bis Tagesende“.
//  - Jeder Kunde höchstens einmal pro Werbeblock; Groß/Klein und Leerzeichen
//    am Rand zählen dabei nicht.

import type { TrafficSlotEintrag } from "./types";

export type WerbeStatus = "laeuft" | "geplant" | "abgelaufen";

/** Heutiges Datum als JJJJ-MM-TT in Ortszeit. */
export function heuteIso(d = new Date()): string {
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function werbeStatus(s: Pick<TrafficSlotEintrag, "starts_at" | "ends_at">, heute: string): WerbeStatus {
  if (s.ends_at && s.ends_at < heute) return "abgelaufen";
  if (s.starts_at && s.starts_at > heute) return "geplant";
  return "laeuft";
}

/** "06:00:00" → Minuten seit Mitternacht; null bleibt null. */
export function minuten(t: string | null): number | null {
  if (!t) return null;
  const [h, m] = t.split(":");
  return Number(h) * 60 + Number(m ?? 0);
}

/** Volle Stunden (0–23), in denen der Spot laufen darf. */
export function erlaubteStunden(s: Pick<TrafficSlotEintrag, "daypart_start" | "daypart_end">): number[] {
  const von = minuten(s.daypart_start) ?? 0;
  const bis = minuten(s.daypart_end) ?? 24 * 60;
  const out: number[] = [];
  for (let h = 0; h < 24; h++) if (von <= h * 60 && h * 60 < bis) out.push(h);
  return out;
}

/** Darf der Spot heute überhaupt in einen Werbeblock? (Zeitraum, Tageszeit, Audio.) */
export function darfHeute(s: TrafficSlotEintrag, heute: string): boolean {
  return werbeStatus(s, heute) === "laeuft" && s.spot_library_id !== null && erlaubteStunden(s).length > 0;
}

/** Braucht Aufmerksamkeit: ohne Audio, abgelaufen, oder eine Tageszeit ohne volle Stunde. */
export function problem(s: TrafficSlotEintrag, heute: string): string | null {
  if (s.spot_library_id === null) return "Kein Spot – läuft nie";
  if (werbeStatus(s, heute) === "abgelaufen") return "Abgelaufen";
  if (erlaubteStunden(s).length === 0) return "Tageszeit enthält keine volle Stunde";
  return null;
}

export function kundenSchluessel(name: string): string {
  return name.trim().toLowerCase();
}

/** Buchungen nach Kunde, Kunden alphabetisch; innerhalb nach Gewicht. */
export function nachKunde<T extends Pick<TrafficSlotEintrag, "advertiser" | "weight">>(slots: T[]): Array<{ kunde: string; slots: T[] }> {
  const gruppen = new Map<string, { kunde: string; slots: T[] }>();
  for (const s of slots) {
    const key = kundenSchluessel(s.advertiser);
    const g = gruppen.get(key) ?? { kunde: s.advertiser.trim(), slots: [] };
    g.slots.push(s);
    gruppen.set(key, g);
  }
  return [...gruppen.values()]
    .map((g) => ({ ...g, slots: [...g.slots].sort((a, b) => b.weight - a.weight) }))
    .sort((a, b) => a.kunde.localeCompare(b.kunde, "de"));
}

/** Kennzahlen oben auf der Seite. */
export function kennzahlen(slots: TrafficSlotEintrag[], heute: string): { aktiv: number; kunden: number; heute: number; probleme: number } {
  const aktiveKunden = new Set(slots.filter((s) => werbeStatus(s, heute) !== "abgelaufen").map((s) => kundenSchluessel(s.advertiser)));
  return {
    aktiv: slots.filter((s) => werbeStatus(s, heute) === "laeuft" && s.spot_library_id !== null).length,
    kunden: aktiveKunden.size,
    heute: slots.filter((s) => darfHeute(s, heute)).length,
    probleme: slots.filter((s) => problem(s, heute) !== null).length,
  };
}

/** Gewicht als 1–5 gefüllte Balken, gemessen am schwersten Spot des Kanals. */
export function gewichtBalken(weight: number, maxWeight: number): number {
  if (maxWeight <= 0) return 1;
  return Math.max(1, Math.min(5, Math.ceil((weight / maxWeight) * 5)));
}

/** 20000 → "0:20" */
export function dauer(ms: number | null | undefined): string {
  if (!ms || ms < 0) return "–";
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** "06:00:00" → "06:00" */
export function uhrzeit(t: string | null): string {
  return t ? t.slice(0, 5) : "";
}

/** "2026-10-01" → "01.10.2026" */
export function datumDe(d: string | null): string {
  if (!d) return "";
  const [j, m, t] = d.split("-");
  return `${t}.${m}.${j}`;
}

/** Tageszeit in Worten. */
export function tageszeitText(s: Pick<TrafficSlotEintrag, "daypart_start" | "daypart_end">): string {
  if (!s.daypart_start && !s.daypart_end) return "ganztägig";
  if (s.daypart_start && s.daypart_end) return `${uhrzeit(s.daypart_start)}–${uhrzeit(s.daypart_end)} Uhr`;
  if (s.daypart_start) return `ab ${uhrzeit(s.daypart_start)} Uhr`;
  return `bis ${uhrzeit(s.daypart_end)} Uhr`;
}

/** Zeitraum in Worten. */
export function zeitraumText(s: Pick<TrafficSlotEintrag, "starts_at" | "ends_at">): string {
  if (!s.starts_at && !s.ends_at) return "unbefristet";
  if (s.starts_at && s.ends_at) return `${datumDe(s.starts_at)} – ${datumDe(s.ends_at)}`;
  if (s.starts_at) return `ab ${datumDe(s.starts_at)}`;
  return `bis ${datumDe(s.ends_at)}`;
}

/** API-Fehlercodes der Werbeplanung in Klartext. */
export const WERBE_FEHLER: Record<string, string> = {
  daypart_invalid: "Die Tageszeit muss vorwärts laufen (z. B. 06:00 bis 10:00). Über Mitternacht geht nicht – dafür zwei Buchungen anlegen.",
  period_invalid: "Das Ende des Zeitraums liegt vor dem Beginn.",
  invalid_date: "Das Datum ist ungültig.",
  spot_not_found: "Den gewählten Spot gibt es in der Mediathek nicht (mehr).",
  traffic_slot_not_found: "Diese Buchung gibt es nicht mehr – vielleicht hat sie jemand gelöscht.",
  channel_not_found: "Diesen Kanal gibt es nicht (mehr).",
};
